import {defineStore} from 'pinia'
import api from '@/services/api'
import {useToastsStore} from '@/stores/toasts'
import {useComponentsStore} from '@/stores/components'
import {useFieldsetsStore} from '@/stores/fieldsets'
import {useModalsStore} from '@/stores/modals'
import {useCategoriesStore} from '@/stores/categories'
import {usePagesStore} from '@/stores/pages'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useItemDataStore} from '@/stores/itemData'
import {useAppStore} from '@/stores/appStore'

export function useFormStore(formId = 'form') {
    return defineStore(formId, {
        state: () => ({
            show: true,
            tableName: undefined,
            id: undefined,
            
            fields: [],
            legends: [],
            
            currentData: {},
            originalData: {},
            
            formEl: undefined,
            isSaving: false,
            hasJustBeenSaved: false,
            onSave: function(item) {
            },
            
            clickedAnchor: undefined,
            showAllFieldsets: true,
            
            formSmall: false,
            singularLabel: 'item',
            
            onDelete: undefined,
            onDeleteConfirm: undefined,
            onDeleteComplete: undefined
        }),
        
        getters: {
            isModified(state) {
                function sortObject(unordered) {
                    if (!unordered) return {}
                    unordered = JSON.parse(JSON.stringify(unordered))
                    
                    return Object.keys(unordered)
                        .sort()
                        .reduce((obj, key) => {
                            obj[key] = unordered[key]
                            return obj
                        }, {})
                }
                
                return JSON.stringify(sortObject(state.currentData)) !== JSON.stringify(sortObject(state.originalData))
            },
            
            modifiedData(state) {
                let modifiedData = false
                if (this.isModified) {
                    let obj = state.currentData
                    for (let prop in obj) {
                        if (Object.prototype.hasOwnProperty.call(obj, prop)) {
                            let newValue = obj[prop]
                            let oldValue = state.originalData ? state.originalData[prop] : undefined
                            
                            if (
                                JSON.stringify(oldValue) !== JSON.stringify(newValue) &&
                                newValue !== undefined
                            ) {
                                if (!modifiedData) {
                                    modifiedData = {}
                                }
                                
                                modifiedData[prop] = newValue
                            }
                        }
                    }
                }
                
                return modifiedData
            },
            
            field: (state) => (fieldName) => {
                return state.fields.find((o) => o.columnName === fieldName)
            },
            
            componentId: (state) => {
                const componentsStore = useComponentsStore()
                return componentsStore.componentIds[state.tableName]
            },
            
            component(state) {
                const componentsStore = useComponentsStore()
                return componentsStore.items.find((obj) => obj.id === this.componentId)
            },
            
            showDisplayOrderOption(state) {
                return !!(
                    this.component?.showDisplayOrder &&
                    state.currentData &&
                    !state.currentData.isArchived &&
                    state.tableName !== 'content'
                )
            }
        },
        
        actions: {
            toggleShow() {
                this.show = false
                setTimeout(() => {
                    this.show = true
                }, 50)
            },
            
            setFormEl(el) {
                this.formEl = el
            },
            
            presetData(d) {
                let name = d.name
                let value = d.value
                
                let originalDataValue =
                    typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value
                
                if (name.indexOf('[') !== -1) {
                    let arr1 = name.split('[')
                    let name1 = arr1[0]
                    let arr2 = arr1[1].split(']')
                    let newName = arr2[0]
                    
                    if (name1 !== 'field') {
                        newName = newName.replace(/["']/g, '')
                        
                        if (this.currentData[name1] === null) {
                            this.currentData[name1] = {}
                        }
                        if (this.originalData[name1] === null) {
                            this.originalData[name1] = {}
                        }
                        
                        this.currentData[name1][newName] = value
                        this.originalData[name1][newName] = originalDataValue
                    }
                } else {
                    this.originalData[name] = originalDataValue
                    this.currentData[name] = value
                }
            },
            
            set(d) {
                this.currentData[d.name] = d.value
            },
            
            setFieldProperty({fieldName, property, value}) {
                const field = this.fields.find((o) => o.columnName === fieldName)
                if (field) {
                    field[property] = value
                }
            },
            
            addField({field}) {
                field.error = ''
                field.visibility = ''
                this.fields.push(field)
            },
            
            applyModifiedData() {
                Object.assign(this.originalData, JSON.parse(JSON.stringify(this.currentData)))
            },
            
            async init(d) {
                const itemDataStore = useItemDataStore()
                
                this.tableName = d.tableName
                this.id = d.id
                Object.assign(this.currentData, d.currentData || {})
                this.onSave = d.onSave || this.onSave
                this.onDelete = d.onDelete
                this.onDeleteConfirm = d.onDeleteConfirm
                this.onDeleteComplete = d.onDeleteComplete
                
                this.singularLabel = d.singularLabel || this.singularLabel
                
                this.fields = []
                this.legends = []
                
                this.formSmall = d.formSmall || false
                
                const obj = await itemDataStore.get({
                    tableName: d.tableName,
                    id: d.id
                })
                
                if (d.id) {
                    Object.assign(this.originalData, JSON.parse(JSON.stringify(obj)))
                    Object.assign(this.currentData, JSON.parse(JSON.stringify(obj)))
                } else if (d.currentData) {
                    Object.assign(this.originalData, JSON.parse(JSON.stringify(d.currentData)))
                    Object.assign(this.currentData, JSON.parse(JSON.stringify(d.currentData)))
                }
                
                const componentStructureStore = useComponentStructureStore()
                const objs = componentStructureStore.get(this.componentId)
                let allFields = JSON.parse(JSON.stringify(objs))
                
                allFields.forEach((o) => (o.error = ''))
                allFields.forEach((o) => (o.visibility = ''))
                
                let fields = []
                if (d.fieldNames && d.fieldNames.length) {
                    let matchedFields
                    d.fieldNames.forEach((fieldName) => {
                        matchedFields = allFields.filter((o) => o.columnName === fieldName)
                        if (matchedFields.length) {
                            let field = matchedFields[0]
                            fields.push(field)
                        }
                    })
                } else {
                    fields = allFields
                }
                
                fields.forEach((field) => this.addField({field}))
                
                let legends = []
                
                const fieldsetsStore = useFieldsetsStore()
                let fieldsets = fieldsetsStore.items
                if (fieldsets.length) {
                    const fieldsetIdsInUse = fields.map((o) => o.fieldsetId)
                    fieldsets = fieldsets.filter(
                        (o) => o.componentId === this.componentId && fieldsetIdsInUse.indexOf(o.id) > -1
                    )
                    legends = fieldsets.map((o) => o.legend)
                    
                    if (this.component?.showMetadata) legends.push('SEO')
                    if (this.component?.showScheduling) legends.push('Scheduling')
                } else {
                    if (fields.length) {
                        legends = fields.filter((o) => o.type === 'fieldset').map((o) => o.title)
                        
                        if (fields[0].type !== 'fieldset') {
                            legends.unshift('General')
                        }
                    }
                }
                
                this.legends = legends
                
                if (d.onInit) d.onInit()
            },
            
            setDefaultValue(d) {
                let name = d.name
                
                if (
                    this.currentData[name] === '' ||
                    this.currentData[name] === null ||
                    this.currentData[name] === undefined
                ) {
                    this.presetData(d)
                }
            },
            
            async save() {
                const modifiedData = this.id ? this.modifiedData : this.currentData
                
                if (this.currentData.id && !modifiedData) {
                    return Promise.resolve()
                }
                
                const isValid = await this.validateForm()
                if (!isValid) {
                    return Promise.resolve({invalid: true})
                }
                
                if (this.tableName.indexOf('product_variations__') === 0) {
                    return this.saveVariations()
                }
                
                this.isSaving = true
                
                const o = this.id
                    ? await api.patch(`api/component/${this.tableName}/${this.id}`, modifiedData)
                    : await api.post(`api/component/${this.tableName}`, modifiedData)
                
                const item = o.data
                
                this.onSaveAction(item)
                
                this.isSaving = false
                
                return item
            },
            
            validateForm() {
                if (this.formEl && this.formEl.checkValidity()) {
                    return true
                }
                if (!this.formEl) return false
                
                let firstInvalidElement
                
                Array.from(this.formEl.elements).forEach((element) => {
                    let name = element.name
                    if (!name) {
                        return
                    }
                    
                    if (name.indexOf('.') > -1) {
                        name = element.name.split('.')[0]
                    }
                    
                    this.setFieldProperty({
                        fieldName: name,
                        property: 'error',
                        value: element.validationMessage
                    })
                    
                    if (!firstInvalidElement && element.validationMessage) {
                        firstInvalidElement = element
                        element.closest('[data-form-group]').scrollIntoView()
                    }
                    
                    if (element.validationMessage && !this.field(name)) {
                        console.error('Invalid field:', [
                            name,
                            element.validationMessage,
                            element.validity,
                            element
                        ])
                    }
                })
            },
            
            async onSaveAction(item) {
                if (!item) {
                    this.isSaving = false
                    return
                }
                
                const appStore = useAppStore()
                const tableName = this.tableName
                const itemId = item.id
                
                this.hasJustBeenSaved = true
                
                appStore.cacheNeedsClearing = true
                
                const itemDataStore = useItemDataStore()
                itemDataStore.set({tableName, id: itemId, property: item})
                
                const categoriesStore = useCategoriesStore()
                const columnNames = this.fields
                    .filter((o) => o.type === 'relationshipManyToMany')
                    .map((o) => o.columnName)
                
                for (const k in columnNames) {
                    const columnName = columnNames[k]
                    
                    const catIds = item[columnName]
                    if (catIds !== undefined) {
                        const data = []
                        catIds.forEach((catId, k) => data.push({itemId, catId, displayOrder: k + 1}))
                        
                        categoriesStore.setItemData({tableName, columnName, itemId, data})
                    }
                }
                
                this.applyModifiedData()
                
                if (tableName === 'pages') {
                    const pagesStore = usePagesStore()
                    await pagesStore.getClassNames({refresh: true})
                }
                
                this.onSave(item)
            },
            
            delete({siteTree}) {
                if (this.onDelete) {
                    this.onDelete()
                    return
                }
                
                const modalsStore = useModalsStore()
                modalsStore.show({
                    componentName: 'ConfirmModal',
                    obj: {
                        modalTitle: 'Delete ' + this.singularLabel,
                        modalBody: `
                <p class="alert alert-danger">This <strong>cannot</strong> be undone.</p>
                <p>Are you sure?</p>
            `,
                        onConfirm: () => {
                            if (this.onDeleteConfirm) {
                                this.onDeleteConfirm()
                                return
                            }
                            
                            const itemDataStore = useItemDataStore()
                            itemDataStore
                                .delete({
                                    tableName: this.tableName,
                                    id: this.id
                                })
                                .then(() => {
                                    if (this.onDeleteComplete) {
                                        this.onDeleteComplete()
                                        return
                                    }
                                    
                                    window.location = siteTree ? '/#/site_tree/' : '/#/' + this.tableName
                                })
                        }
                    }
                })
            },
            
            async saveVariations() {
                const toastsStore = useToastsStore()
                const appStore = useAppStore()
                
                const componentStructureStore = useComponentStructureStore()
                const objs = componentStructureStore.get(this.componentId)
                let values = []
                objs.forEach((obj) => {
                    if (obj.columnName.indexOf('option') === 0) {
                        let value = this.currentData[obj.columnName]
                        values.push(value)
                    }
                })
                
                let calculateCartesianProductTotal = (paramArray) => {
                    let total = 0
                    paramArray.forEach((params, key) => {
                        key === 0 ? (total = params.length) : (total = total * params.length)
                    })
                    return total
                }
                
                let total = calculateCartesianProductTotal(values)
                let limit = 20000
                if (total > limit) {
                    toastsStore.add({
                        title: 'Too many variations!',
                        body:
                            '<strong>' +
                            total +
                            '</strong> variations detected.<br>' +
                            'You may create up to <strong>' +
                            limit +
                            '</strong> at a time.'
                    })
                    
                    this.onSaveAction()
                    return
                }
                
                let cartesianProduct = (paramArray) => {
                    let limit = 500000
                    let total = calculateCartesianProductTotal(paramArray)
                    if (total > limit) {
                        console.error('cartesianProduct() is set to not create more than ' + limit + ' items.')
                        return false
                    }
                    
                    function addTo(curr, args) {
                        let i
                        let copy
                        let rest = args.slice(1)
                        let last = !rest.length
                        let result = []
                        
                        for (i = 0; i < args[0].length; i++) {
                            copy = curr.slice()
                            copy.push(args[0][i])
                            
                            last ? result.push(copy) : (result = result.concat(addTo(copy, rest)))
                        }
                        
                        return result
                    }
                    
                    return addTo([], paramArray)
                }
                
                const obj = await api.post('api/variations', {
                    productId: this.currentData.productId,
                    sku: this.currentData.sku,
                    comparisonPrice: this.currentData.comparisonPrice,
                    price: this.currentData.price,
                    variations: cartesianProduct(values)
                })
                
                if (obj.data.duplicates) {
                    toastsStore.add({
                        body: obj.data.duplicates + ' duplicates were not saved.'
                    })
                }
                
                appStore.cacheNeedsClearing = true
                
                this.applyModifiedData()
                
                this.onSaveAction()
            }
        }
    })()
}